import React, { useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { BankIdHintCodeType, LOGIN_CUSTOMER_NOT_FOUND, OTHER_DEVICE, SAME_DEVICE } from '@constants/bankid';
import { ButtonWrapper, PendingAuthWrapper, SpinnerWrapper, StyledFullWidthButton } from './AuthStatuses.styles';
import Text from '@atoms/Text/Text';
import Spinner from '@molecules/Spinner/Spinner';
import { mapFailedTranslationKey, mapPendingTranslationKey } from '@organisms/BankIdLoginFlow/translationHelper';
import { trackBankIdError, trackBankIdLoginCancelled } from '@helpers/analyticsHelpers/trackLogin';
import { getBankidUserMessageKey } from '@helpers/bankidTranslation';

export interface PendingAuthProps {
  handleCancel: () => void;
  hintCode: BankIdHintCodeType;
  isSameDevice: boolean;
}
export interface FailedAuthProps {
  hintCode: BankIdHintCodeType;
  handleReturnClick: () => void;
  isQr: boolean;
}

export const PendingAuth = ({ handleCancel, hintCode, isSameDevice }: PendingAuthProps) => {
  const tBankId = useTranslations('bankid');
  const tLogin = useTranslations('login');
  const key = mapPendingTranslationKey({ hintCode, isSameDevice });
  const translationKey = getBankidUserMessageKey(key);

  return (
    <PendingAuthWrapper data-testid="pending-auth">
      <Text type="body">{translationKey ? tBankId(translationKey) : key}</Text>
      <SpinnerWrapper>
        <Spinner size={32} />
      </SpinnerWrapper>
      <ButtonWrapper>
        <StyledFullWidthButton
          variant="link"
          size="small"
          onClick={() => {
            trackBankIdLoginCancelled('Pending Statuses');
            handleCancel();
          }}
        >
          {tLogin('bankId.cancel')}
        </StyledFullWidthButton>
      </ButtonWrapper>
    </PendingAuthWrapper>
  );
};

export const FailedAuth = ({ handleReturnClick, hintCode, isQr }: FailedAuthProps) => {
  const tBankId = useTranslations('bankid');
  const tLogin = useTranslations('login');
  const key = mapFailedTranslationKey({ isQr, hintCode });
  const translationKey = getBankidUserMessageKey(key);

  useEffect(() => {
    const message = translationKey ? tBankId(translationKey) : key;
    trackBankIdError(isQr ? OTHER_DEVICE : SAME_DEVICE, message);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PendingAuthWrapper data-testid="failed-auth">
      <Text type="body">{translationKey ? tBankId(translationKey) : key}</Text>
      <ButtonWrapper>
        <StyledFullWidthButton disabled={hintCode === LOGIN_CUSTOMER_NOT_FOUND} onClick={handleReturnClick}>
          {tLogin('bankId.return')}
        </StyledFullWidthButton>
      </ButtonWrapper>
    </PendingAuthWrapper>
  );
};

export const CompleteAuth = () => {
  const t = useTranslations('login');

  return (
    <PendingAuthWrapper data-testid="pending-auth">
      <Text type="body">{t('bankId.loggingIn')}</Text>
      <SpinnerWrapper>
        <Spinner size={32} />
      </SpinnerWrapper>
    </PendingAuthWrapper>
  );
};
